import { ReactNode, useState, useCallback, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEvents } from '../../hooks/useEvents';
import { useAppStore } from '../../stores/appStore';
import { toast } from 'sonner';

interface MobilePullToRefreshProps {
  children: ReactNode;
  onRefresh?: () => Promise<void>;
  disabled?: boolean;
}

const MobilePullToRefresh = ({ 
  children, 
  onRefresh,
  disabled = false 
}: MobilePullToRefreshProps) => {
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startY, setStartY] = useState(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  
  const { selectedDate } = useAppStore();
  const { refetch } = useEvents({ date: selectedDate });
  
  const THRESHOLD = 80;

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (disabled || isRefreshing) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > 10) return;
    
    setStartY(e.touches[0].clientY);
    setIsPulling(true);
  }, [disabled, isRefreshing]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPulling || disabled || isRefreshing) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > 0) {
      setIsPulling(false);
      setPullDistance(0);
      return;
    }
    
    const currentY = e.touches[0].clientY;
    const distance = Math.max(0, currentY - startY);
    
    if (distance > 0) {
      // Prevent default only when pulling down at the top
      e.preventDefault();
      setPullDistance(Math.min(distance, THRESHOLD * 1.5));
    }
  }, [isPulling, disabled, isRefreshing, startY]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled || isRefreshing) return;
    
    const shouldRefresh = pullDistance >= THRESHOLD;
    
    if (shouldRefresh) {
      setIsRefreshing(true);
      setPullDistance(THRESHOLD);
      
      try {
        if (onRefresh) {
          await onRefresh();
        } else {
          await refetch();
        }
        
        toast.success(t('pullToRefresh.updated'));
      } catch (error) {
        console.error('Refresh failed:', error);
        toast.error(t('errors.network'));
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
    
    setIsPulling(false);
  }, [isPulling, disabled, isRefreshing, pullDistance, onRefresh, refetch, t]);

  useEffect(() => {
    // Only add listeners on mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) return;

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const getRefreshText = () => {
    if (isRefreshing) return t('pullToRefresh.refreshing');
    if (pullDistance >= THRESHOLD) return t('pullToRefresh.release');
    if (pullDistance > 20) return t('pullToRefresh.pull');
    return '';
  };

  const opacity = Math.min(1, pullDistance / THRESHOLD);
  const scale = 0.5 + (0.5 * Math.min(1, pullDistance / THRESHOLD));

  return (
    <div className="relative">
      {/* Pull to Refresh Indicator */}
      {(pullDistance > 0 || isRefreshing) && (
        <div 
          className="absolute top-0 left-0 right-0 z-10 flex items-center justify-center bg-primary-50 border-b border-primary-100 transition-all"
          style={{ 
            opacity,
            height: `${pullDistance}px`,
            transform: `translateY(0)`,
          }}
        >
          <div className="flex items-center space-x-2 text-primary-600 font-medium">
            <div style={{ transform: `scale(${scale})` }}>
              <RefreshCw 
                className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} 
              />
            </div>
            <span className="text-sm">
              {getRefreshText()}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div 
        className="relative"
        style={{
          transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : 'none',
          transition: isPulling ? 'none' : 'transform 0.2s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default MobilePullToRefresh;